import React, { useEffect, useState, useCallback } from "react";
import { organizationsAPI } from "../../utils/organizationsAPI";
import { showToast } from "../../components/Toast";
import Pagination from "../../components/Pagination";

const DEFAULT_ORG = {
  name: "",
  description: "",
  contact_email: "",
  contact_phone: "",
  region: "",
};

function ManageOrganizations() {
  const [organizations, setOrganizations] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState(DEFAULT_ORG);
  const [loading, setLoading] = useState(false);
  const [editingOrg, setEditingOrg] = useState(null);
  const [editForm, setEditForm] = useState(DEFAULT_ORG);
  const [saving, setSaving] = useState(false);

  const loadOrganizations = useCallback(async (page = 1, searchTerm = "") => {
    try {
      const params = { page, page_size: 15 };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      const response = await organizationsAPI.list(params);
      const data = response?.data || response;
      setOrganizations(Array.isArray(data) ? data : data?.results || []);
      if (response?.pagination) setPagination(response.pagination);
    } catch (err) {
      showToast.error(err.message || "Failed to load organizations");
    }
  }, []);

  useEffect(() => {
    loadOrganizations(currentPage, search);
  }, [currentPage, search, loadOrganizations]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;

    try {
      setLoading(true);
      await organizationsAPI.create({
        name: form.name.trim(),
        description: form.description?.trim() || "",
        contact_email: form.contact_email?.trim() || "",
        contact_phone: form.contact_phone?.trim() || "",
        region: form.region?.trim() || "",
      });
      showToast.success("Organization created successfully");
      setForm(DEFAULT_ORG);
      setCurrentPage(1);
      loadOrganizations(1, search);
    } catch (err) {
      showToast.error(err.message || "Failed to create organization");
    } finally {
      setLoading(false);
    }
  };

  const openEdit = (org) => {
    setEditingOrg(org);
    setEditForm({
      name: org.name || "",
      description: org.description || "",
      contact_email: org.contact_email || "",
      contact_phone: org.contact_phone || "",
      region: org.region || "",
    });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingOrg) return;
    try {
      setSaving(true);
      await organizationsAPI.update(editingOrg.id, {
        name: editForm.name.trim(),
        description: editForm.description?.trim() || "",
        contact_email: editForm.contact_email?.trim() || "",
        contact_phone: editForm.contact_phone?.trim() || "",
        region: editForm.region?.trim() || "",
      });
      showToast.success("Organization updated successfully");
      setEditingOrg(null);
      loadOrganizations(currentPage, search);
    } catch (err) {
      showToast.error(err.message || "Failed to update organization");
    } finally {
      setSaving(false);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="page-content">
      <div className="card" style={{ maxWidth: "640px" }}>
        <h2 className="card-title">Manage Organizations</h2>
        <form
          onSubmit={handleCreate}
          style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }}
        >
          <div className="form-group">
            <label>Name</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Organization name"
              required
            />
          </div>
          <div className="form-group">
            <label>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              placeholder="Short description"
              rows={2}
            />
          </div>
          <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <div className="form-group" style={{ flex: "1 1 200px" }}>
              <label>Contact email</label>
              <input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm((p) => ({ ...p, contact_email: e.target.value }))}
                placeholder="contact@example.com"
              />
            </div>
            <div className="form-group" style={{ flex: "1 1 200px" }}>
              <label>Contact phone</label>
              <input
                type="text"
                value={form.contact_phone}
                onChange={(e) => setForm((p) => ({ ...p, contact_phone: e.target.value }))}
                placeholder="+250 ..."
              />
            </div>
          </div>
          <div className="form-group">
            <label>Region</label>
            <input
              type="text"
              value={form.region}
              onChange={(e) => setForm((p) => ({ ...p, region: e.target.value }))}
              placeholder="Region or location"
            />
          </div>
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? "Creating..." : "Add Organization"}
          </button>
        </form>
      </div>

      {editingOrg && (
        <div className="card" style={{ marginTop: "24px", maxWidth: "640px" }}>
          <h3 className="card-title">Edit: {editingOrg.name}</h3>
          <form onSubmit={handleUpdate} style={{ display: "flex", flexDirection: "column", gap: "16px", marginTop: "16px" }}>
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={editForm.name}
                onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                required
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={editForm.description}
                onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="form-group">
              <label>Contact email</label>
              <input
                type="email"
                value={editForm.contact_email}
                onChange={(e) => setEditForm((p) => ({ ...p, contact_email: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Contact phone</label>
              <input
                type="text"
                value={editForm.contact_phone}
                onChange={(e) => setEditForm((p) => ({ ...p, contact_phone: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label>Region</label>
              <input
                type="text"
                value={editForm.region}
                onChange={(e) => setEditForm((p) => ({ ...p, region: e.target.value }))}
              />
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "Saving..." : "Save"}</button>
              <button type="button" className="btn btn-secondary" onClick={() => setEditingOrg(null)}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      <div className="card" style={{ marginTop: "24px" }}>
        <h3 className="card-title">Organizations</h3>
        <div className="form-group" style={{ maxWidth: "320px", marginBottom: "16px" }}>
          <label>Search</label>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, region, or contact..."
          />
        </div>
        {organizations.length > 0 ? (
          <>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Contact / Region</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {organizations.map((org) => (
                  <tr key={org.id}>
                    <td>
                      <div>{org.name}</div>
                      {org.description && (
                        <div style={{ fontSize: "12px", color: "var(--text-grey)", marginTop: "4px" }}>
                          {org.description.slice(0, 60)}{org.description.length > 60 ? "…" : ""}
                        </div>
                      )}
                    </td>
                    <td>
                      {org.contact_email && <div style={{ fontSize: "13px" }}>{org.contact_email}</div>}
                      {org.contact_phone && <div style={{ fontSize: "12px", color: "var(--text-grey)" }}>{org.contact_phone}</div>}
                      {org.region && <div style={{ fontSize: "12px", color: "var(--text-grey)" }}>{org.region}</div>}
                      {!org.contact_email && !org.contact_phone && !org.region && "-"}
                    </td>
                    <td>
                      {org.created_at
                        ? new Date(org.created_at).toLocaleDateString()
                        : "-"}
                    </td>
                    <td>
                      <button type="button" className="btn btn-sm btn-secondary" onClick={() => openEdit(org)}>
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
            />
          </>
        ) : (
          <p style={{ padding: "16px", textAlign: "center" }}>
            No organizations yet.
          </p>
        )}
      </div>
    </div>
  );
}

export default ManageOrganizations;
